"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { userService, CustomerLookupOption } from "@/services/userService";

/** Sentinel for the "no customer filter" row; cannot collide with a User id. */
const ALL_CUSTOMERS_VALUE = "__all_customers__";

interface CustomerFilterComboboxProps {
  /** Selected customer's User id, or "" for no filter. */
  value: string;
  onChange: (customerId: string) => void;
}

/**
 * Type-ahead customer picker for the billing filters.
 *
 * Queries the server rather than reading the customer list from context: that
 * list is empty for staff users (CustomerContext skips loading it for
 * non-admins) and the billing page is staff-accessible, so a context-fed
 * dropdown would render blank for them.
 */
export function CustomerFilterCombobox({
  value,
  onChange,
}: CustomerFilterComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [options, setOptions] = useState<CustomerLookupOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Held separately from `options` so the trigger keeps showing the chosen
  // customer even once the result list has been replaced by a new search.
  const [selected, setSelected] = useState<CustomerLookupOption | null>(null);

  // Guards against a slow early request overwriting a later one's results.
  const requestIdRef = useRef(0);

  const fetchOptions = useCallback(async (term: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const results = await userService.lookupCustomers(term || undefined);
      if (requestId === requestIdRef.current) {
        setOptions(results);
      }
    } catch (error) {
      console.error("Error looking up customers:", error);
      if (requestId === requestIdRef.current) {
        setOptions([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Only query while the popover is open -- no point loading a list nobody
  // is looking at.
  useEffect(() => {
    if (open) {
      fetchOptions(debouncedSearch);
    }
  }, [open, debouncedSearch, fetchOptions]);

  // Keep the label in step when the filter is cleared from outside.
  useEffect(() => {
    if (!value) {
      setSelected(null);
    }
  }, [value]);

  const handleSelect = (customer: CustomerLookupOption) => {
    setSelected(customer);
    onChange(customer.id);
    setOpen(false);
  };

  /**
   * Resets to "all customers".
   *
   * Shared by the clear button and the "All Customers" list item so the two
   * routes out of a selection cannot drift apart.
   */
  const clearSelection = useCallback(() => {
    setSelected(null);
    setSearch("");
    onChange("");
  }, [onChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/*
        The clear button is a sibling of the trigger, not a child of it.
        PopoverTrigger asChild merges its own onClick onto the Button, and that
        handler runs before any nested element's -- so a clear control inside
        the trigger only ever toggles the popover.
      */}
      <div className="flex gap-2">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex-1 justify-between font-normal"
          >
            <span className="truncate">
              {selected ? selected.name : "All Customers"}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {value && (
          <Button
            variant="outline"
            size="icon"
            onClick={clearSelection}
            title="Clear customer filter"
            aria-label="Clear customer filter"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        {/* Results come from the server already filtered; cmdk's own
            filtering would then hide valid matches. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search name or phone..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {loading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Searching...
              </div>
            ) : (
              <>
                <CommandEmpty>No customers found.</CommandEmpty>
                <CommandGroup>
                  {/* The second way out of a selection, alongside the clear
                      button next to the trigger. */}
                  <CommandItem
                    value={ALL_CUSTOMERS_VALUE}
                    onSelect={() => {
                      clearSelection();
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value ? "opacity-0" : "opacity-100",
                      )}
                    />
                    <span className="flex-1">All Customers</span>
                  </CommandItem>
                  {options.map((customer) => (
                    <CommandItem
                      key={customer.id}
                      value={customer.id}
                      onSelect={() => handleSelect(customer)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === customer.id ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="flex-1 truncate">{customer.name}</span>
                      {customer.phone && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {customer.phone}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
